import { Search, Shield, GraduationCap, FolderSearch } from "lucide-react";
import { PresetTemplate, DorkGenerationOptions } from "@/types/dork";
import { useLocale } from "@/contexts/LocaleContext";

interface PresetTemplatesProps {
  onSelect: (options: Partial<DorkGenerationOptions>, template: PresetTemplate) => void;
  activeTemplate: PresetTemplate | null;
}

export function PresetTemplates({ onSelect, activeTemplate }: PresetTemplatesProps) {
  const { t } = useLocale();

  const presets = [
    {
      id: "osint" as PresetTemplate,
      name: t("preset.osint_name"),
      description: t("preset.osint_desc"),
      icon: Search,
      color: "from-cyan-500/20 to-blue-500/20",
      borderColor: "border-cyan-500/30",
      textColor: "text-cyan-400",
      options: {
        sensitivityLevel: "medium" as const,
      },
    },
    {
      id: "security" as PresetTemplate,
      name: t("preset.security_name"),
      description: t("preset.security_desc"),
      icon: Shield,
      color: "from-green-500/20 to-emerald-500/20",
      borderColor: "border-green-500/30",
      textColor: "text-green-400",
      options: {
        sensitivityLevel: "high" as const,
        fileTypes: ["sql", "log", "txt", "json"],
      },
    },
    {
      id: "academic" as PresetTemplate,
      name: t("preset.academic_name"),
      description: t("preset.academic_desc"),
      icon: GraduationCap,
      color: "from-purple-500/20 to-pink-500/20",
      borderColor: "border-purple-500/30",
      textColor: "text-purple-400",
      options: {
        sensitivityLevel: "low" as const,
        fileTypes: ["pdf", "docx"],
        domains: ["edu", "ac.uk", "academia.edu", "researchgate.net"],
      },
    },
    {
      id: "file-discovery" as PresetTemplate,
      name: t("preset.file_name"),
      description: t("preset.file_desc"),
      icon: FolderSearch,
      color: "from-orange-500/20 to-amber-500/20",
      borderColor: "border-orange-500/30",
      textColor: "text-orange-400",
      options: {
        sensitivityLevel: "high" as const,
        fileTypes: ["pdf", "xlsx", "csv", "sql", "log"],
      },
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {presets.map((preset) => {
        const Icon = preset.icon;
        const isActive = activeTemplate === preset.id;
        
        return (
          <button
            key={preset.id}
            onClick={() => onSelect(preset.options, preset.id)}
            className={`relative p-4 rounded-xl border text-left transition-all duration-300 group ${
              isActive
                ? `bg-gradient-to-br ${preset.color} ${preset.borderColor} shadow-lg`
                : "bg-muted/20 border-border/30 hover:border-border hover:bg-muted/30"
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`p-2 rounded-lg transition-colors ${
                  isActive
                    ? `bg-gradient-to-br ${preset.color}`
                    : "bg-muted/50 group-hover:bg-muted"
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? preset.textColor : "text-muted-foreground"}`} />
              </div>
              <div>
                <h3
                  className={`text-sm font-semibold mb-0.5 ${
                    isActive ? preset.textColor : "text-foreground"
                  }`}
                >
                  {preset.name}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {preset.description}
                </p>
              </div>
            </div>
            {isActive && (
              <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${preset.textColor} animate-pulse`} 
                style={{ backgroundColor: 'currentColor' }} />
            )}
          </button>
        );
      })}
    </div>
  );
}
